"use client";

import { useRef } from "react";
import { useInView } from "framer-motion";
import { TrendUpIcon } from "@phosphor-icons/react/ssr";
import Image from "next/image";
import ImprovementChart from "~/componentsDS/LPComponents/components/ImprovementChart";
import Heading from "~/componentsDS/Typography/Heading";
import Lead from "~/componentsDS/Typography/Lead";
import Overline from "~/componentsDS/Typography/Overline";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import RollingNumber from "./RollingNumber";

const stats = [
    {
        value: "10,000",
        suffix: "+",
        label: <>Libida<sup>®</sup> users</>,
    },
    {
        value: "60",
        suffix: <span className="text-[20px] md:text-[28px]">min</span>,
        label: <>on-demand window</>,
    },
    {
        value: "72",
        suffix: <span className="text-[20px] md:text-[28px]">hrs</span>,
        label: <>results can last</>,
    },
];

const legend = [
    { label: "Desire score", color: "#38357F" },
    { label: "Satisfying events per month", color: "#4A46A9" },
    { label: "Arousal score", color: "#A4A2D4" },
];

export default function StatsChart() {
    const statsRef = useRef<HTMLDivElement>(null);
    const isVisible = useInView(statsRef, { once: true, amount: 0.35 });

    return (
        <div className="relative text-primary500 lg:h-[568px] lg:rounded-[12px] backdrop-blur-[25px] lg:px-6 lg:pt-6 overflow-hidden">
            <Image
                src="https://res.cloudinary.com/dirscuuhp/image/upload/v1789990263/new_design/Frame_2147205572_n6tetf.webp"
                alt=""
                fill
                quality={95}
                sizes="(min-width: 768px) 700px, 0px"
                loading="lazy"
                className="max-md:hidden object-cover object-center"
            />
            <div className="relative">
                <div ref={statsRef} className="grid grid-cols-3 gap-2">
                    {stats.map((stat, index) => (
                        <div
                            key={index}
                            className="flex h-[112px] flex-col items-center md:justify-center pt-7 md:pt-0 rounded-[8px] bg-[#4A46A9] text-center text-pureWhite lg:h-[130px]"
                        >
                            <Lead size="sm" sizes={{ md: "md" }} font="featureDisplay" className="tracking-[0.72px] whitespace-nowrap">
                                <RollingNumber value={stat.value} animate={isVisible} />
                                {stat.suffix}
                            </Lead>
                            <Paragraph size="sm" sizes={{ md: "lg" }}>
                                {stat.label}
                            </Paragraph>
                        </div>
                    ))}
                </div>

                <div className="relative mt-2 overflow-hidden rounded-[8px] bg-[#e9edf7] lg:mt-3 lg:rounded-[10px]">
                    <div className="absolute left-4 top-4 z-[1] flex items-center gap-2 lg:left-6 lg:top-6">
                        <span className="flex size-9 items-center justify-center rounded-[7px] lg:rounded-[10px] bg-[#4A46A9] lg:size-[52px]">
                            <TrendUpIcon className="size-6 text-pureWhite lg:size-9" />
                        </span>
                        <Heading as="span" size="3xs" sizes={{ md: "xs" }} weight="400">
                            Improved desire <br />
                            in just 1–8 weeks
                        </Heading>
                    </div>
                    <ImprovementChart ariaLabel="Animated bar chart showing improved desire in one to eight weeks" />
                </div>

                <ul className="mt-3 flex flex-wrap items-center justify-around gap-x-1 gap-y-2 lg:mt-4 lg:flex-nowrap">
                    {legend.map((item) => (
                        <li
                            key={item.label}
                            className="flex items-center gap-2"
                        >
                            <span
                                className="size-2.5 shrink-0 rounded-full lg:size-[14px]"
                                style={{ backgroundColor: item.color }}
                            />
                            <Overline size="sm" className="whitespace-nowrap pt-[2px] text-pureWhite">
                                {item.label}
                            </Overline>
                        </li>
                    ))}
                </ul>

                <Paragraph size="2xs" className="mt-6 text-center text-pureWhite/50 md:hidden">
                    Individual results may vary. Results often felt by 2nd or 3rd attempt. <br />
                    Libida<sup>®</sup> is compounded on an individual basis and these statements have not been evaluated by the FDA.
                </Paragraph>
            </div>
        </div>
    );
}
